import React, { useCallback, useEffect, useState, CSSProperties } from "react";
import { Image, Spinner } from "react-bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { api_url } from "../api/config/config";
import CompanyBlock from "../components/CompanyBlock";
import { Company } from "../types/type";

interface DataStructure {
    counts: { [key: number]: number };
    company: Company[];
}

interface ResponseStructure {
    success: boolean;
    data: DataStructure;
}

const Bidding: React.FC = () => {
    const backendURL = api_url;

    const { userInfo, userToken } = useSelector((state: RootState) => state.auth);
    const { vendorInfo } = useSelector((state: RootState) => state.vendor);

    const [counts, setCounts] = useState<{ [key: number]: number }>({ 1: 0, 2: 0, 3: 0, 4: 0 });
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [cache, setCache] = useState<{ [key: string]: Company[] }>({}); // ← 追加

    const PER_PAGE = 9;
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const status = parseInt(queryParams.get('status') || '1', 10);

    const fetchCompanies = useCallback(async () => {
        const cacheKey = `${status}-${page}`;
        if (cache[cacheKey]) {
            // キャッシュにあればそれを使う
            setCompanies(cache[cacheKey]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${backendURL}/getBids`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userInfo?.id,
                    vendor_id: vendorInfo?.vendor_id,
                    limit: PER_PAGE,
                    offset: (page - 1) * PER_PAGE,
                    status,
                    userToken
                }),
            });

            const res: ResponseStructure = await response.json();
            // counts は初回ロード時だけ更新
            if (Object.values(counts).every(v => v === 0)) {
                setCounts(res.data.counts || {});
            }

            setCompanies(res.data.company || []);
            setCache(prev => ({
                ...prev,
                [cacheKey]: res.data.company || []
            }));
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    }, [status, page, userToken, userInfo?.id, vendorInfo?.vendor_id, backendURL, cache, counts]);

    useEffect(() => {
        fetchCompanies();
    }, [status, page, fetchCompanies]);
    
    // status が変わったら page をリセット
    useEffect(() => {
        setPage(1);
    }, [status]);

    const BiddingLinks = [
        {
            text: '見積書アップロード無',
            icon: 'icon-chat.png',
            color: 'text-orange',
            wrapperColor: 'bg-item-wrapper-orange',
            companiesCount: counts[1] || 0
        },
        {
            text: '見積書アップロード済',
            icon: 'icon-certificate.png',
            color: 'text-orange',
            wrapperColor: 'bg-item-wrapper-orange',
            companiesCount: counts[2] || 0
        },
    ];

    const handleClickStatus = (status: number) => {
        navigate(`/bidding?status=${status}`);
    };

    const getLeftPercentage = (status: number): string => {
        return `${status * 20}%`; // 1→20%, 2→40%, etc.
    };

    const bubbleStyle = {
        '--bubble-left': getLeftPercentage(status),
    } as React.CSSProperties;

    const renderCompanies = () => {
        return companies.map((company: Company, index: React.Key) => (
            <div className="col-lg-4 mb-3" key={index}>
                <CompanyBlock company={company} />
            </div>
        ));
    };

    const totalPages = Math.ceil((counts[status] || 0) / PER_PAGE);

    return (
        <>
            <section className="section-1 py-lg-5 py-4 info-dispaly">
                <div className="container">
                    <div className="row mx-0">
                        <div className="col-lg-12 mx-auto px-0">
                            <div className="text-center mb-lg-4 mb-3">
                                <img className="img img-fluid height-90 mb-2" src="assets/images/bid-wining/title-page-cetificate.svg" />
                                <div className="text-md mb-sm-4 mb-3 fw-400">応札中および落札案件タスク一覧</div>
                            </div>
                            <div className="py-3">
                                <div className="row item-section pb-5">
                                    <div className="col-lg-9 mx-auto px-0">
                                        <div className="row list-auction">
                                            {BiddingLinks.map((link, index) => (
                                                <div className="col-lg-3" key={index} onClick={() => handleClickStatus(index + 1)}>
                                                    <NavLink style={{ color: "black", textDecoration: "none" }} to="#">
                                                        <div className={`bg-item-bid-wrapper ${link.wrapperColor} mb-lg-0 mb-3 ${index + 1 === status ? "active" : ""}`}>
                                                            <p className={`text-xs ${link.color} mb-2`}>{link.text}</p>
                                                            <div className="text-xs fw-400">
                                                                <Image className="item-img me-2" src={`assets/images/bid-wining/${link.icon}`} />
                                                                <span className="text-extra-xl">{link.companiesCount} <em>件</em></span>
                                                            </div>
                                                        </div>
                                                    </NavLink>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={`bubble`} style={bubbleStyle}></div>
                            <div className="bg_box-inquiry p-4">
                                <div className="tab-container">
                                    <div id="tab-1" className="tab-content">
                                        <div className="row">
                                            {loading ? (
                                                <div className="d-flex justify-content-center align-items-center mb-5" style={{ width: "100%", height: "100%" }}>
                                                    <Spinner animation="border" variant="primary" />
                                                </div>
                                            ) : (
                                                renderCompanies()
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav aria-label="Page navigation example">
                        <ul className="pagination justify-content-center">
                            {page > 1 && (
                                <li className="page-item">
                                    <a className="page-link" href="#" onClick={() => setPage(page - 1)}>＜ 前へ</a>
                                </li>
                            )}
                            {[...Array(totalPages)].map((_, i) => (
                                <li className={`page-item ${page === i + 1 ? "active" : ""}`} key={i}>
                                    <a className="page-link" href="#" onClick={() => setPage(i + 1)}>{i + 1}</a>
                                </li>
                            ))}
                            {page < totalPages && (
                                <li className="page-item">
                                    <a className="page-link" href="#" onClick={() => setPage(page + 1)}>次へ ＞</a>
                                </li>
                            )}
                        </ul>
                    </nav>
                </div>
            </section>
        </>
    );
};

export default Bidding;
